export interface RecordComparisonRequest {
  peak_type: string;
  metric_type?: 'value' | 'wkg';
  start_date?: string;
  end_date?: string;
  workout_type_id?: number;
  comparison_scope?: 'selected' | 'all_time';
  gender?: 'all' | 'male' | 'female';
  age_group?: string;
}
export interface RecordComparisonResult {
  status: 'ready' | 'no_record' | 'insufficient_data';
  minimum_peers: number;
  is_preliminary?: boolean;
  peer_count: number | null;
  own_value: number | null;
  lower_is_better: boolean;
  better_percent?: number;
  equal_percent?: number;
  worse_percent?: number;
  top_ten_value?: number;
  gap_to_top_ten?: number;
  distribution?: {min: number; max: number; bins: number[]};
  updated_at: string;
}
export function comparisonPosition(
  value: number,
  min: number,
  max: number,
  lower: boolean,
) {
  if (min === max) return value === min ? 0.5 : value > min !== lower ? 1 : 0;
  const position = Math.max(
    0,
    Math.min(1, (value - min) / Math.max(0.00001, max - min)),
  );
  return lower ? 1 - position : position;
}

export function recordStandingPercent(data: RecordComparisonResult) {
  // "Better than" excludes tied athletes; it is not simply 100 minus "better".
  return Math.max(
    0,
    Math.min(
      100,
      Math.round(
        (data.worse_percent ??
          100 - (data.better_percent ?? 0) - (data.equal_percent ?? 0)) * 10,
      ) / 10,
    ),
  );
}

export function recordDistributionGeometry(data: RecordComparisonResult) {
  const distribution = data.distribution;
  if (!distribution || !distribution.bins.length || data.own_value == null)
    return null;
  const {min, max} = distribution;
  const bins =
    min === max
      ? [distribution.bins.reduce((a, b) => a + b, 0)]
      : data.lower_is_better
      ? [...distribution.bins].reverse()
      : distribution.bins;
  const highest = Math.max(...bins, 1);
  const step = 324 / bins.length;
  const position = comparisonPosition(
    data.own_value,
    min,
    max,
    data.lower_is_better,
  );
  const bars = bins.map((count, i) => ({
    count,
    x: 18 + (i + 0.5) * step,
    height: (count / highest) * 105,
    width: Math.min(13, step * 0.72),
    ahead: (i + 0.5) / bins.length > position,
  }));
  const outline = `M18 176 ${bars
    .map(bar => `L${bar.x} ${176 - bar.height}`)
    .join(' ')} L342 176`;
  const ticks = (min === max ? [0.5] : [0, 0.25, 0.5, 0.75, 1]).map(
    fraction => ({
      x: 18 + fraction * 324,
      value:
        min + (data.lower_is_better ? 1 - fraction : fraction) * (max - min),
    }),
  );
  return {bars, outline, ticks, marker: 18 + position * 324};
}
